import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { getAdminRecord, observeAuth } from "../lib/firebase";
import { translations } from "../data/sampleContent";

const PreferencesContext = createContext(null);
const AuthContext = createContext(null);

export function PreferencesProvider({ children }) {
  const [mode, setMode] = useState(() => localStorage.getItem("satnam-theme") || "light");
  const [language, setLanguage] = useState(() => localStorage.getItem("satnam-language") || "en");

  useEffect(() => {
    localStorage.setItem("satnam-theme", mode);
    document.documentElement.dataset.theme = mode;
  }, [mode]);

  useEffect(() => {
    localStorage.setItem("satnam-language", language);
    document.documentElement.lang = language;
  }, [language]);

  const value = useMemo(() => ({
    mode,
    setMode,
    language,
    setLanguage,
    t: translations[language]
  }), [mode, language]);

  return <PreferencesContext.Provider value={value}>{children}</PreferencesContext.Provider>;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => observeAuth(async (nextUser) => {
    setLoading(true);
    setUser(nextUser);
    if (!nextUser) {
      setAdmin(null);
      setLoading(false);
      return;
    }
    try {
      const record = await getAdminRecord(nextUser.uid);
      setAdmin(record?.active ? record : null);
    } catch {
      setAdmin(null);
    } finally {
      setLoading(false);
    }
  }), []);

  const value = useMemo(() => ({
    user,
    admin,
    isAdmin: Boolean(user && admin?.active),
    loading
  }), [user, admin, loading]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const usePreferences = () => useContext(PreferencesContext);
export const useAuth = () => useContext(AuthContext);
